import { auth } from '../firebase';
import { API_BASE_URL } from '../config';
import { writeAuditLogSafe } from './auditLogService';

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

export const updateUserProfile = async ({ uid, name, username, password, role, purchasedCredits }) => {
  const targetUid = String(uid || '').trim();
  if (!targetUid) {
    const err = new Error('Missing user id.');
    err.code = 'MISSING_UID';
    throw err;
  }

  const currentUser = auth.currentUser;
  if (!currentUser) {
    const err = new Error('Not signed in.');
    err.code = 'AUTH_REQUIRED';
    throw err;
  }

  const idToken = await currentUser.getIdToken(true);
  const storedUser = getStoredUser();
  const actor = {
    uid: currentUser.uid || null,
    email: currentUser.email || 'unknown',
    role: storedUser?.role || 'unknown',
    name: storedUser?.name || null,
    username: storedUser?.username || null,
    isAuthenticated: true,
  };

  const body = {
    name,
    username,
    password: password || '',
  };

  if (role) {
    body.role = role;
  }

  if (purchasedCredits !== undefined) {
    body.purchasedCredits = purchasedCredits;
  }

  const resp = await fetch(`${API_BASE_URL}/api/users/${encodeURIComponent(targetUid)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });

  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok || payload?.ok !== true) {
    const message = payload?.error || 'Failed to update user.';

    await writeAuditLogSafe({
      action: 'User profile update failed',
      details: message,
      severity: 'Warning',
      category: 'user_management',
      outcome: 'failure',
      actor,
      resource: {
        type: 'user',
        id: targetUid,
        name: username || name || null,
        path: `/api/users/${encodeURIComponent(targetUid)}`,
      },
      metadata: {
        attemptedRole: role || null,
        fieldsChanged: {
          name: typeof name === 'string',
          username: typeof username === 'string',
          password: Boolean(password),
          role: Boolean(role),
        },
      },
      tags: ['user-management', 'update', 'failed'],
    });

    const err = new Error(message);
    err.code = 'USER_UPDATE_FAILED';
    throw err;
  }

  // Backend already records a successful profile update audit entry.
  // Avoid duplicate frontend log on success to keep a single canonical record.

  return payload;
};

export const addUserCredits = async ({ uid, amount }) => {
  const targetUid = String(uid || '').trim();
  const creditsToAdd = Number(amount);
  if (!targetUid) throw new Error('Missing user id.');
  if (!Number.isInteger(creditsToAdd) || creditsToAdd <= 0) {
    throw new Error('Credits to add must be a positive whole number.');
  }

  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Not signed in.');

  const idToken = await currentUser.getIdToken(true);
  const resp = await fetch(`${API_BASE_URL}/api/users/${encodeURIComponent(targetUid)}/credits`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ amount: creditsToAdd }),
  });

  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok || payload?.ok !== true) {
    throw new Error(payload?.error || 'Failed to add credits.');
  }

  return payload;
};